/**
 * ================================================================================
 * COMPONENTE: Sidebar (Desktop + Mobile Drawer)
 * PROPÓSITO: Navegação principal lateral com links, ícones e secções.
 *            Suporta modos: Modern, Compact, Classic, Floating, Glass.
 *            Suporta layout mobile: BottomNav (default) ou Sidebar (Drawer).
 * ================================================================================
 */
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Wallet, ArrowLeftRight, Grid3X3, PieChart,
  Target, RefreshCcw, BarChart3, Settings, LogOut, Shield, Users
} from 'lucide-react';
import { useFinance } from '../../context/FinanceContext';
import { useTheme } from '../../context/ThemeContext';

const mainLinks = [
  { to: '/',              icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/wallets',       icon: Wallet,          label: 'Carteiras' },
  { to: '/transactions',  icon: ArrowLeftRight,  label: 'Transações' },
  { to: '/categories',    icon: Grid3X3,         label: 'Categorias' },
];

const planningLinks = [
  { to: '/budgets',       icon: PieChart,     label: 'Orçamentos' },
  { to: '/goals',         icon: Target,       label: 'Metas' },
  { to: '/recurring',     icon: RefreshCcw,   label: 'Recorrentes' },
  { to: '/reports',       icon: BarChart3,    label: 'Relatórios' },
];

export default function Sidebar() {
  const { logout, sidebarOpen, toggleSidebar, user } = useFinance();
  const { sidebarMode, mobileLayout, mobileLayouts } = useTheme();
  const location = useLocation();

  // Se mobileLayout for 'bottom-nav', a sidebar só aparece no desktop (hidden on mobile via CSS default)
  // Mas se for 'sidebar', ela deve funcionar como drawer no mobile
  const isMobileSidebar = mobileLayout === mobileLayouts.SIDEBAR;

  return (
    <>
      {/* Overlay mobile */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={toggleSidebar}
          aria-hidden="true"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            zIndex: 49, display: sidebarOpen ? 'block' : 'none',
          }}
        />
      )}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''} variant-${sidebarMode} ${isMobileSidebar ? 'mobile-enabled' : ''}`}>
        {/* Brand */}
        <div className="sidebar-brand">
          <img src="/kumbuos_logo_1771707161700.png" alt="KumbuOs Logo" className="sidebar-brand-logo" style={{ background: 'transparent', padding: 2 }} />
          <span className="sidebar-brand-name">KumbuOs</span>
        </div>

        <nav className="sidebar-nav">
          {/* Main */}
          <div className="sidebar-section-title">Principal</div>
          {mainLinks.map((link) => {
            const Icon = link.icon;
            return (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/'}
                className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                onClick={() => sidebarOpen && toggleSidebar()}
              >
                <Icon className="sidebar-link-icon" />
                <span>{link.label}</span>
              </NavLink>
            );
          })}

          {/* Planning */}
          <div className="sidebar-section-title">Planeamento</div>
          {planningLinks.map((link) => {
            const Icon = link.icon;
            return (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                onClick={() => sidebarOpen && toggleSidebar()}
              >
                <Icon className="sidebar-link-icon" />
                <span>{link.label}</span>
              </NavLink>
            );
          })}

          {/* Administração */}
          {user?.is_super_admin && (
            <>
              <div className="sidebar-section-title">Administração</div>
              <NavLink
                to="/admin/dashboard"
                className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                onClick={() => sidebarOpen && toggleSidebar()}
              >
                <Shield className="sidebar-link-icon" style={{ color: 'var(--accent-blue)' }} />
                <span>Painel Admin</span>
              </NavLink>
              <NavLink
                to="/admin/users"
                className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                onClick={() => sidebarOpen && toggleSidebar()}
              >
                <Users className="sidebar-link-icon" style={{ color: 'var(--accent-blue)' }} />
                <span>Gestão de Utilizadores</span>
              </NavLink>
            </>
          )}

          {/* Settings */}
          <div className="sidebar-section-title">Sistema</div>
          <NavLink
            to="/settings"
            className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
          >
            <Settings className="sidebar-link-icon" />
            <span>Configurações</span>
          </NavLink>

          <button className="sidebar-link" onClick={logout} style={{ marginTop: 'auto' }}>
            <LogOut className="sidebar-link-icon" />
            <span>Terminar Sessão</span>
          </button>
        </nav>
      </aside>

      <style>{`
        /* Floating Variant */
        .sidebar.variant-floating {
          margin: 16px;
          height: calc(100vh - 32px);
          border-radius: 24px;
          box-shadow: 0 10px 40px -10px rgba(0,0,0,0.1);
          border: 1px solid var(--border-primary);
        }

        /* Glass Variant */
        .sidebar.variant-glass {
          background: rgba(var(--bg-sidebar-rgb), 0.7);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-right: 1px solid rgba(255,255,255,0.1);
        }

        /* Mobile Layout: Sidebar Mode */
        @media (max-width: 1024px) {
          .sidebar.mobile-enabled {
            display: flex !important; /* Force sidebar existence, but hidden by default transform */
            transform: translateX(-100%);
            transition: transform 0.3s ease;
            z-index: 50;
            position: fixed;
            height: 100%;
            top: 0; left: 0;
            width: 280px;
            border-right: none;
            box-shadow: 0 0 20px rgba(0,0,0,0.2);
          }
          .sidebar.mobile-enabled.open {
            transform: translateX(0);
          }

          /* Ensure Floating matches mobile properly (reset margin) */
          .sidebar.variant-floating.mobile-enabled {
             margin: 0;
             height: 100%;
             border-radius: 0;
          }
        }
      `}</style>
    </>
  );
}
